import React, { Children, useState, useRef, useSyncExternalStore } from 'react';

import Tooltip from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import moment from 'moment';
import 'styles/DigiformaStaffingBadge.css';

// Shared across every badge of the page: only one tooltip can be pinned (opened by a click) at a time
let pinnedBadgeId = null;
const pinnedListeners = new Set();
let badgeIdCounter = 0;

const setPinnedBadgeId = (id) => {
  pinnedBadgeId = id;
  pinnedListeners.forEach((listener) => listener());
};

const subscribePinnedBadge = (listener) => {
  pinnedListeners.add(listener);
  return () => pinnedListeners.delete(listener);
};

const getPinnedBadgeId = () => pinnedBadgeId;

export const isDigiformaStaffed = (staffing) => staffing?.status === 'full' || staffing?.status === 'partial';

// The tooltip is rendered in a portal, outside of the badge: it's styled through its own class
const tooltipSlotProps = {
  tooltip: { className: 'digiforma-tooltip' }
};

const TooltipContent = ({ modules }) => (
  <div>
    <div className="digiforma-tooltip-title">Staffing Digiforma</div>
    {Children.toArray(modules.map((module) => {
      const staffing = module.digiformaStaffing;
      const status = staffing?.status ?? 'none';
      return (
        <div className="digiforma-tooltip-module">
          <span className={`digiforma-tooltip-dot ${status}`} />
          <div className="digiforma-tooltip-module-body">
            <span className="digiforma-tooltip-module-name">{module.name}</span>
            {status === 'partial' &&
              <span className="digiforma-tooltip-partial"> · partiellement staffé</span>
            }
            {status === 'none' &&
              <div className="digiforma-tooltip-none">Non staffé dans Digiforma</div>
            }
            {isDigiformaStaffed(staffing) && Children.toArray(staffing.instructors.map((instructor) => (
              <div className="digiforma-tooltip-instructor">
                <PersonOutlineIcon className="digiforma-tooltip-instructor-icon" />
                <span>{instructor.firstname} {instructor.lastname}</span>
                <span className="digiforma-tooltip-dates">
                  · {instructor.dates.map((date) => moment(date).format('DD/MM/YYYY')).join(', ')}
                </span>
              </div>
            )))}
          </div>
        </div>
      );
    }))}
  </div>
);

// Read-only indicator of the staffing entered in Digiforma.
// modules: [{ name, digiformaStaffing }] — one module (search / result screens) or every module of a training (list)
// infoIcon: display an "i" icon instead of the "DF" badge
// The tooltip shows on hover, and stays open once the badge is clicked (until a click elsewhere).
const DigiformaStaffingBadge = (props) => {
  const { modules = [], infoIcon = false } = props;
  const badgeId = useRef(null);
  if (badgeId.current === null) badgeId.current = ++badgeIdCounter;
  const [hovered, setHovered] = useState(false);
  const currentPinnedId = useSyncExternalStore(subscribePinnedBadge, getPinnedBadgeId);
  const pinned = currentPinnedId === badgeId.current;
  // Hovering opens the tooltip only when no other one is pinned
  const open = pinned || (hovered && currentPinnedId === null);

  const staffedModules = modules.filter((module) => isDigiformaStaffed(module.digiformaStaffing));
  if (staffedModules.length === 0) return (<></>);

  const isPartial = staffedModules.some((module) => module.digiformaStaffing.status === 'partial');
  const badgeClassName = [
    'digiforma-badge',
    infoIcon ? 'info-icon' : 'chip',
    isPartial ? 'partial' : 'full',
    pinned ? 'pinned' : ''
  ].join(' ').trim();

  const handleClick = (e) => {
    // Avoid triggering the parent click (e.g. row navigation in the training list)
    e.stopPropagation();
    // Pinning this one closes the one currently pinned, if any
    setPinnedBadgeId(pinned ? null : badgeId.current);
  };

  const handleClickAway = () => {
    // Read the live value: when clicking another badge, it has already been pinned at this point
    if (getPinnedBadgeId() === badgeId.current) setPinnedBadgeId(null);
  };

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <span className="digiforma-badge-wrapper">
        <Tooltip
          title={<TooltipContent modules={modules} />}
          placement="bottom-start"
          arrow
          open={open}
          slotProps={tooltipSlotProps}
        >
          <span
            className={badgeClassName}
            onClick={handleClick}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            {infoIcon ? <InfoOutlinedIcon className="digiforma-badge-icon" /> : 'DF'}
          </span>
        </Tooltip>
      </span>
    </ClickAwayListener>
  );
};

export default DigiformaStaffingBadge;
